// src/components/roads/ponding/RoadPondingPanel.js
// FlowSense AI card above the Road Works map: where rain will settle on the road network.
//   • signal + live rain forecast (Open-Meteo, same run as the drainage outlook)
//   • hourly chart: rain bars + number of segments with standing water; click / scrub / play an hour
//   • the worst segments (depth, when water starts, how long it stays, why, what to do) — click to fly there
//   • method, assumptions and data gaps, so the operator knows how far to trust it
import React, { memo, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, AlertDescription, AlertIcon, Badge, Box, Button, Collapse, Flex, HStack, Icon, IconButton, SimpleGrid, Skeleton,
    Slider, SliderFilledTrack, SliderThumb, SliderTrack, Switch, Text, Tooltip, VStack, useColorModeValue,
} from '@chakra-ui/react';
import { MdWaterDrop, MdRefresh, MdPlayArrow, MdPause, MdAutoAwesome, MdExpandMore, MdExpandLess, MdOpenInNew, MdMyLocation } from 'react-icons/md';
import { BAND_KEYS, SIGNAL_META, bandMeta, bandAt, depthAt, fmtDepth, fmtWater, fmtHours, fmtClears, timeLabel, KIND_LABEL } from './pondingUi';

const Stat = ({ label, value, sub, color }) => {
    const muted = useColorModeValue('gray.600', 'gray.400');
    return (
        <Box>
            <Text fontSize="11px" fontWeight="700" color={muted} textTransform="uppercase" letterSpacing="0.04em">{label}</Text>
            <Text fontSize="xl" fontWeight="900" color={color} lineHeight="1.2">{value ?? '—'}</Text>
            {sub && <Text fontSize="11px" color={muted} noOfLines={1}>{sub}</Text>}
        </Box>
    );
};
Stat.propTypes = { label: PropTypes.string, value: PropTypes.node, sub: PropTypes.node, color: PropTypes.string };

/** Rain bars (mm/h) + segments-with-water line, one shared x axis; click an hour to show it on the map. */
const HourChart = memo(({ timeline, hour, onHour }) => {
    const bar = useColorModeValue('#90CDF4', '#2C5282');
    const barSel = useColorModeValue('#2B6CB0', '#63B3ED');
    const line = '#805AD5';
    const grid = useColorModeValue('#E2E8F0', 'rgba(255,255,255,0.08)');
    const muted = useColorModeValue('#718096', '#A0AEC0');
    const tipBg = useColorModeValue('white', 'gray.700');
    const [hover, setHover] = useState(null);
    if (!timeline?.length) return null;
    const W = 600; const H = 90; const n = timeline.length; const w = W / n;
    const maxR = Math.max(1, ...timeline.map((t) => t.rain_mm || 0));
    const maxS = Math.max(1, ...timeline.map((t) => t.segments_ponding || 0));
    const hv = hover != null ? timeline[hover] : null;
    return (
        <Box position="relative">
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" role="img"
                aria-label="Forecast rainfall per hour and road segments with standing water" style={{ display: 'block', cursor: 'pointer' }}
                onMouseLeave={() => setHover(null)}>
                <line x1="0" x2={W} y1={H - 1} y2={H - 1} stroke={grid} />
                {timeline.map((t, i) => {
                    const h = ((t.rain_mm || 0) / maxR) * (H - 8);
                    return (
                        <g key={t.hour} onMouseEnter={() => setHover(i)} onClick={() => onHour(hour === i ? null : i)}>
                            <rect x={i * w} y={0} width={w} height={H} fill="transparent" />
                            <rect x={i * w + 1} y={H - h} width={Math.max(1, w - 2)} height={h} rx="2" fill={hour === i ? barSel : bar} />
                        </g>
                    );
                })}
                <polyline fill="none" stroke={line} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round"
                    points={timeline.map((t, i) => `${i * w + w / 2},${H - 2 - ((t.segments_ponding || 0) / maxS) * (H - 10)}`).join(' ')} />
            </svg>
            <Flex justify="space-between" fontSize="10px" color={muted} mt={0.5}>
                <Text>{timeLabel(timeline[0].time)}</Text>
                <HStack spacing={3}>
                    <HStack spacing={1}><Box w="10px" h="10px" borderRadius="2px" bg={bar} /><Text>Rain mm/h</Text></HStack>
                    <HStack spacing={1}><Box w="10px" h="2px" bg={line} /><Text>Segments with water</Text></HStack>
                </HStack>
                <Text>{timeLabel(timeline[n - 1].time)}</Text>
            </Flex>
            {hv && (
                <Box position="absolute" top={0} right={0} bg={tipBg} borderWidth="1px" borderRadius="md" px={2} py={1} boxShadow="sm" pointerEvents="none">
                    <Text fontSize="11px" fontWeight="800">{timeLabel(hv.time)} · +{hv.hour} h</Text>
                    <Text fontSize="11px">{hv.rain_mm} mm rain · {hv.segments_ponding} segment(s) with water{hv.max_depth_m ? ` · max ${fmtDepth(hv.max_depth_m)}` : ''}</Text>
                </Box>
            )}
        </Box>
    );
});
HourChart.displayName = 'HourChart';
HourChart.propTypes = { timeline: PropTypes.array, hour: PropTypes.number, onHour: PropTypes.func };

function RoadPondingPanel({ forecast: f, loading, error, stale, onRefresh, hour, onHour, playing, onPlay, showOnMap, onShowOnMap, onFocusSegment, onOpenReport, maxItems = 8 }) {
    const card = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('blue.100', 'whiteAlpha.200');
    const muted = useColorModeValue('gray.600', 'gray.400');
    const rowHover = useColorModeValue('gray.50', 'whiteAlpha.100');
    const [showMethod, setShowMethod] = useState(false);
    const s = f?.summary || {};
    const sig = SIGNAL_META[f?.signal] || SIGNAL_META.none;
    const list = useMemo(() => {
        const segs = f?.segments || [];
        if (hour == null) return segs.slice(0, maxItems);
        return [...segs].filter((x) => bandAt(x, hour) !== 'none').sort((a, b) => depthAt(b, hour) - depthAt(a, hour)).slice(0, maxItems);
    }, [f, hour, maxItems]);
    const hourInfo = hour != null ? f?.timeline?.[hour] : null;

    return (
        <Box bg={card} borderWidth="1px" borderColor={border} borderRadius="2xl" p={4} position="relative" overflow="hidden" role="region" aria-label="Road ponding forecast">
            <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, cyan.400, blue.500, purple.500)" />
            <Flex justify="space-between" align="center" gap={2} wrap="wrap" mb={3}>
                <HStack spacing={2}>
                    <Flex w={9} h={9} borderRadius="lg" bgGradient="linear(135deg, cyan.400, blue.600)" align="center" justify="center"><Icon as={MdWaterDrop} color="white" boxSize={5} /></Flex>
                    <Box>
                        <HStack spacing={2}>
                            <Text fontWeight="900" fontSize="md">Where rain will settle on the roads</Text>
                            <Badge colorScheme="purple" fontSize="9px"><Icon as={MdAutoAwesome} boxSize={2.5} mr={0.5} />AI</Badge>
                        </HStack>
                        <Text fontSize="12px" color={muted}>
                            Next {f?.horizon_hours ?? 24} h · live rainfall {f?.rain?.source ? `(${f.rain.source})` : ''} × drainage outlook × TessaDEM road profile
                            {f?.generated_at ? ` · updated ${new Date(f.generated_at).toLocaleTimeString()}` : ''}{stale ? ' · refreshing' : ''}
                        </Text>
                    </Box>
                </HStack>
                <HStack spacing={2}>
                    {f && <Badge colorScheme={sig.scheme} variant="solid" borderRadius="full" px={2.5} py={0.5}>{sig.label}</Badge>}
                    <HStack spacing={1}>
                        <Switch size="sm" isChecked={showOnMap} onChange={(e) => onShowOnMap?.(e.target.checked)} id="ponding-on-map" />
                        <Text as="label" htmlFor="ponding-on-map" fontSize="12px" fontWeight="600">On map</Text>
                    </HStack>
                    <Tooltip label="Pull the latest rainfall forecast and recompute drainage + roads now" hasArrow>
                        <IconButton size="sm" variant="ghost" icon={<Icon as={MdRefresh} />} aria-label="Recompute the road ponding forecast" isLoading={loading} onClick={() => onRefresh?.(true)} />
                    </Tooltip>
                </HStack>
            </Flex>

            {error && !f && (
                <Alert status="warning" borderRadius="lg"><AlertIcon /><AlertDescription fontSize="sm">{error}</AlertDescription></Alert>
            )}
            {!f && !error && <Skeleton h="160px" borderRadius="lg" />}

            {f && (
                <VStack align="stretch" spacing={3}>
                    <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3}>
                        <Stat label="Segments with water" value={s.ponding_segments ?? 0}
                            sub={BAND_KEYS.filter((k) => s.by_band?.[k]).map((k) => `${s.by_band[k]} ${bandMeta(k).label.toLowerCase()}`).join(' · ') || `${s.segments_evaluated ?? 0} evaluated`}
                            color={s.ponding_segments ? bandMeta(f.segments?.[0]?.band).hex : undefined} />
                        <Stat label="Roads affected" value={s.roads_affected ?? 0} sub={`${s.roads_evaluated ?? 0} roads checked`} />
                        <Stat label="Water starts" value={fmtHours(s.earliest_ponding_hours)} sub={s.earliest_ponding_hours != null ? 'first standing water' : 'no standing water'} />
                        <Stat label="Deepest" value={s.max_depth_m ? fmtDepth(s.max_depth_m) : '—'} color={s.max_depth_m >= 0.2 ? 'purple.500' : s.max_depth_m >= 0.1 ? 'blue.500' : undefined} />
                        <Stat label="Stays after rain" value={s.standing_after_rain ?? 0} sub="segments ≥ 6 h or longer" />
                        <Stat label="Sags without drain" value={s.sag_segments_without_drain ?? 0} sub={`${s.sags ?? 0} low points found`} color={s.sag_segments_without_drain ? 'orange.500' : undefined} />
                    </SimpleGrid>

                    <Box>
                        <HourChart timeline={f.timeline} hour={hour} onHour={onHour} />
                        <HStack spacing={3} mt={2}>
                            <IconButton size="sm" icon={<Icon as={playing ? MdPause : MdPlayArrow} />} aria-label={playing ? 'Pause playback' : 'Play the forecast hour by hour'} onClick={() => onPlay?.(!playing)} />
                            <Slider aria-label="Forecast hour shown on the map" min={0} max={Math.max(0, (f.timeline?.length || 1) - 1)} step={1} value={hour ?? 0}
                                onChange={(v) => onHour?.(v)} flex={1} focusThumbOnChange={false}>
                                <SliderTrack><SliderFilledTrack bg="blue.400" /></SliderTrack>
                                <SliderThumb boxSize={4} />
                            </Slider>
                            <Button size="sm" variant={hour == null ? 'solid' : 'outline'} colorScheme="blue" onClick={() => onHour?.(null)}>Peak</Button>
                        </HStack>
                        <Text fontSize="12px" color={muted} mt={1}>
                            {hourInfo ? `Showing ${timeLabel(hourInfo.time)} (+${hourInfo.hour} h): ${hourInfo.rain_mm} mm rain, ${hourInfo.segments_ponding} segment(s) with standing water.` : 'Showing the deepest water each segment reaches in the forecast window.'}
                        </Text>
                    </Box>

                    <Box>
                        <Text fontSize="sm" fontWeight="800" mb={1}>{list.length ? 'Where the water will be' : 'No standing water forecast on the mapped roads'}</Text>
                        <VStack align="stretch" spacing={1}>
                            {list.map((x) => {
                                const band = bandAt(x, hour);
                                const b = bandMeta(band === 'none' ? x.band : band);
                                return (
                                    <Flex key={x.segment_id} gap={3} align="center" p={2} borderRadius="lg" _hover={{ bg: rowHover }} cursor="pointer" onClick={() => onFocusSegment?.(x)}
                                        role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') onFocusSegment?.(x); }}
                                        aria-label={`${x.road_name} segment ${x.index}: ${fmtWater(x, hour)}`}>
                                        <Box w="8px" alignSelf="stretch" borderRadius="full" bg={b.hex} flexShrink={0} />
                                        <Box flex={1} minW={0}>
                                            <HStack spacing={2}>
                                                <Text fontSize="sm" fontWeight="800" noOfLines={1}>{x.road_name}</Text>
                                                <Text fontSize="12px" color={muted}>seg {x.index ?? '—'}</Text>
                                                <Badge colorScheme={b.scheme} fontSize="10px">{b.label}</Badge>
                                            </HStack>
                                            <Text fontSize="12px" color={muted} noOfLines={1}>
                                                {KIND_LABEL[x.kind] || 'Water on the road'}{x.time_to_ponding_hours != null ? ` · starts ${fmtHours(x.time_to_ponding_hours)}` : ''} · {fmtClears(x)}
                                            </Text>
                                            {(x.causes?.[0] || x.cause) && <Text fontSize="12px" noOfLines={1}>{x.causes?.[1] || x.causes?.[0] || x.cause}</Text>}
                                        </Box>
                                        <Text fontSize={x.kind === 'gutter_flow' && !(depthAt(x, hour) > 0.005) ? 'sm' : 'lg'} fontWeight="900" color={b.hex} minW="72px" textAlign="right">{fmtWater(x, hour)}</Text>
                                        <Tooltip label="Fly to it on the map" hasArrow><IconButton size="xs" variant="ghost" icon={<Icon as={MdMyLocation} />} aria-label="Fly to segment" onClick={(e) => { e.stopPropagation(); onFocusSegment?.(x); }} /></Tooltip>
                                        {onOpenReport && <Tooltip label="Open the Works Report for this road" hasArrow><IconButton size="xs" variant="ghost" icon={<Icon as={MdOpenInNew} />} aria-label="Open works report" onClick={(e) => { e.stopPropagation(); onOpenReport(x); }} /></Tooltip>}
                                    </Flex>
                                );
                            })}
                        </VStack>
                        {(f.segments_total || 0) > list.length && hour == null && <Text fontSize="12px" color={muted} mt={1}>{f.segments_total - list.length} more on the map.</Text>}
                    </Box>

                    {(f.warnings || []).length > 0 && (
                        <Alert status="info" borderRadius="lg" py={2}><AlertIcon /><AlertDescription fontSize="12px">{f.warnings.join(' ')}</AlertDescription></Alert>
                    )}

                    <Box>
                        <Button size="xs" variant="ghost" rightIcon={<Icon as={showMethod ? MdExpandLess : MdExpandMore} />} onClick={() => setShowMethod((v) => !v)}>How this forecast works</Button>
                        <Collapse in={showMethod} animateOpacity>
                            <VStack align="stretch" spacing={1} mt={1} fontSize="12px" color={muted}>
                                <Text>{f.method}</Text>
                                {(f.assumptions || []).map((a) => <Text key={a}>• {a}</Text>)}
                                {f.drainage?.signal && <Text>• Drainage outlook signal: <b>{f.drainage.signal}</b> ({f.drainage.collectors} wells / catch basins / drains).</Text>}
                                <Text>• Bands: {BAND_KEYS.map((k) => `${bandMeta(k).label} — ${bandMeta(k).meaning}`).join(' ')}</Text>
                            </VStack>
                        </Collapse>
                    </Box>
                </VStack>
            )}
        </Box>
    );
}

RoadPondingPanel.propTypes = {
    forecast: PropTypes.object, loading: PropTypes.bool, error: PropTypes.string, stale: PropTypes.bool, onRefresh: PropTypes.func,
    hour: PropTypes.number, onHour: PropTypes.func, playing: PropTypes.bool, onPlay: PropTypes.func,
    showOnMap: PropTypes.bool, onShowOnMap: PropTypes.func, onFocusSegment: PropTypes.func, onOpenReport: PropTypes.func, maxItems: PropTypes.number,
};

export default memo(RoadPondingPanel);
